import React from 'react';
import EmptyState from '../components/EmptyState';
import InfiniteScrollWaypoint from '../components/InfiniteScrollWaypoint';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { DraftsOrder } from '../graphql/generated/graphql';
import CreatePostButton from '../post/CreatePostButton';
import PostRow from '../post/PostRow';

const DRAFT_FRAGMENT = gql(`
  fragment DraftsList_drafts on Post {
    id
    userViewedAt
    insertedAt
    updatedAt
    ...PostRow_post
  }
`);

const METADATA_ATTRIBUTE = {
  [DraftsOrder.RecentlyViewed]: 'userViewedAt',
  [DraftsOrder.RecentlyUpdated]: 'updatedAt',
  [DraftsOrder.RecentlyCreated]: 'insertedAt',
  [DraftsOrder.Alphabetical]: 'updatedAt',
} as const;

interface DraftsListProps<Draft extends DocumentType<typeof DRAFT_FRAGMENT>> {
  drafts: Draft[];
  fetchMoreDrafts: (() => void) | null;
  orderBy: DraftsOrder;
}

const DraftsList = <Draft extends DocumentType<typeof DRAFT_FRAGMENT>>({
  drafts,
  fetchMoreDrafts,
  orderBy,
}: DraftsListProps<Draft>) => {
  return (
    <>
      {drafts.length === 0 ? (
        <EmptyState
          imageName="posts"
          title="Nothing yet."
          message="So, write something!"
          action={
            <CreatePostButton size="large" variant="secondary">
              Start writing
            </CreatePostButton>
          }
        />
      ) : (
        <>
          {drafts.map((post) => (
            <PostRow
              key={post.id}
              post={post}
              meta={
                <PostRow.Meta
                  attribute={METADATA_ATTRIBUTE[orderBy]}
                  values={{
                    userViewedAt: post.userViewedAt,
                    updatedAt: post.updatedAt,
                    insertedAt: post.insertedAt,
                  }}
                />
              }
            />
          ))}
        </>
      )}
      <InfiniteScrollWaypoint fetchMore={fetchMoreDrafts} />
    </>
  );
};

export default DraftsList;
